<template>
  <div class="instructor-dashboard-page">
    <!-- Header -->
    <div class="viva-page-header-with-action">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="instructor || ''"
        current-portal="instructor"
        :disable-portal-link="!isVivaAdmin"
      />
    </div>

    <!-- Instructor Info Section -->
    <div class="instructor-info">
      <div class="instructor-row">
        <h2>Instructor:</h2>
        <span class="instructor-tag-header">{{ formatName(instructor || '') }}</span>
      </div>
      <h1 class="viva-page-title">COURSES</h1>
    </div>

    <!-- Course Cards Grid -->
    <div class="viva-card-grid">
      <!-- Loading state -->
      <div v-if="loading" class="empty-state">
        <i class="glyphicon glyphicon-refresh spinning"></i>
        <h3>Loading courses...</h3>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="empty-state">
        <i class="glyphicon glyphicon-exclamation-sign"></i>
        <h3>Error</h3>
        <p>{{ error }}</p>
        <button class="viva-btn viva-btn-green" @click="loadCourses">Try Again</button>
      </div>

      <template v-else>
        <div
          v-for="course in visibleCourses"
          :key="course.id"
          class="viva-card viva-card-green"
          @click="viewCourse(course.id)"
        >
          <div class="card-title">{{ course.title }}</div>
          <div class="card-subtitle" v-if="course.term || course.year">
            {{ course.term }}{{ course.term && course.year ? ', ' : '' }}{{ course.year }}
          </div>
          <div class="card-instructors" v-if="course.instructors && course.instructors.length > 1">
            <div class="card-instructors-label">Instructors:</div>
            <div class="card-instructors-tags">
              <span
                v-for="(inst, idx) in course.instructors"
                :key="idx"
                class="instructor-tag"
              >{{ formatName(inst.name || inst.email) }}</span>
            </div>
          </div>
        </div>

        <!-- Create New Course Card -->
        <div
          class="viva-card viva-card-green viva-card-create"
          @click="createNewCourse"
        >
          <div class="card-create-content">
            <span class="create-icon">+</span>
            <div class="card-title">CREATE NEW COURSE</div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import { handleApiError } from '../utils/errorHandler.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

export default {
  name: 'InstructorDashboard',
  components: { ProfileDropdown },

  data() {
    return {
      courses: [],
      instructor: null,
      loading: true,
      error: null,
      isVivaAdmin: false,
    };
  },

  computed: {
    visibleCourses() {
      return this.courses.filter(course => course.title.toLowerCase() !== 'create a course');
    }
  },

  mounted() {
    const statusData = getStatusData();
    if (statusData) {
      this.isVivaAdmin = statusData.is_viva_admin || false;
    }
    this.loadCourses();
  },

  methods: {
    async loadCourses() {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiClient.get('/api/viva/instructor/courses/');

        if (response.data && response.data.courses) {
          this.courses = response.data.courses;
          this.instructor = response.data.instructor;
        }
      } catch (error) {
        console.error('Failed to load courses:', error);
        this.error = handleApiError(error, this.$router, 'Failed to load courses. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    createNewCourse() {
      this.$router.push({ name: 'course-edit', params: { id: 'new' } });
    },

    editCourse(courseId) {
      this.$router.push({ name: 'course-edit', params: { id: courseId } });
    },

    viewCourse(courseId) {
      this.$router.push({ name: 'course-assignments', params: { id: courseId } });
    },

    formatName(name) {
      if (name.includes(', ')) {
        const [last, first] = name.split(', ');
        return `${first} ${last}`;
      }
      return name;
    }
  }
};
</script>

<style scoped>
.instructor-dashboard-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
  padding-bottom: 50px;
}

.instructor-info {
  margin-bottom: 0;
  padding-bottom: 10px;
}

.instructor-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 40px;
}

.instructor-row h2 {
  margin: 0;
  white-space: nowrap;
}

.instructor-tag-header {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  background-color: rgba(0, 0, 0, 0.06);
  padding: 4px 12px;
  border-radius: 12px;
}

.viva-card-grid {
  padding-top: 10px;
  margin-left: 30px;
  margin-right: 30px;
  padding-left: 30px;
  padding-right: 30px;
}

.card-instructors {
  margin-top: 10px;
}

.card-instructors-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--viva-grey, #888);
  margin-bottom: 4px;
}

.card-instructors-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.instructor-tag {
  font-size: 11px;
  color: #444;
  background-color: rgba(0, 0, 0, 0.06);
  padding: 2px 8px;
  border-radius: 12px;
  line-height: 1.4;
}
</style>
